'use client'

import { AlertCircle, ArrowLeft, CheckCircle2, DownloadCloud, MapPin } from 'lucide-react'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { MAX_WAYPOINTS } from '@/lib/constants'
import {
	clearImportedStops,
	getImportedStopIds,
	markStopsImported,
} from '@/lib/import/imported-tracker'
import type { ImportableStop } from '@/lib/import/types'
import type { Waypoint } from '@/lib/types'
import { useRouteStore } from '@/stores/route-store'

interface StopSelectStepProps {
	/** The stops to render, already extracted/normalized by the upstream source. */
	stops: ImportableStop[]
	/**
	 * Stable key identifying THIS import source (a tour id, an upload's content
	 * hash, a paste's hash). Keys the imported-tracker so the "already imported"
	 * memory and next-leg default are scoped to the exact source being imported.
	 */
	sourceKey: string
	/** Closes the parent dialog after a successful import. */
	onClose: () => void
	/** When provided, renders a "Back" affordance to return to the previous step. */
	onBack?: () => void
}

/**
 * The import populates origin + destination + waypoints, so the maximum number
 * of importable stops is the waypoint ceiling plus those two endpoints. Keeping
 * this derivation here (rather than hardcoding) means a change to MAX_WAYPOINTS
 * automatically tightens the cap shown in the UI.
 */
const MAX_IMPORTABLE_STOPS = MAX_WAYPOINTS + 2

/** Minimum stops a route needs to be meaningful (an origin and a destination). */
const MIN_IMPORTABLE_STOPS = 2

/**
 * Subtle "already imported" reminder pill. Rendered next to the label of a stop
 * the user has imported on a previous batch — a green-check nudge (per the
 * brand's "reminder" affordance), not a blocker: the row stays toggleable.
 */
function ImportedPill() {
	return (
		<span className="mt-0.5 inline-flex w-fit items-center gap-1 rounded-full bg-success/10 px-1.5 py-0.5 text-xs font-medium text-success">
			<CheckCircle2 className="size-3 shrink-0" aria-hidden />
			Imported
		</span>
	)
}

/**
 * A single stop row. Resolved stops are selectable via the checkbox; unresolved
 * stops are disabled with a "couldn't locate" note since they have no
 * coordinates to route. Already-imported stops show a subtle "Imported" pill but
 * stay fully toggleable (the user may re-import a leg). `label`/`sublabel` are
 * user-controlled source data — rendered as React children (auto-escaped).
 */
function StopRow({
	stop,
	checked,
	imported,
	onToggle,
}: {
	stop: ImportableStop
	checked: boolean
	imported: boolean
	onToggle: (id: string) => void
}) {
	const rowId = `mt-stop-${stop.id}`
	return (
		<div className="flex items-start gap-2 rounded-md px-2 py-2">
			<Checkbox
				id={rowId}
				className="mt-0.5"
				checked={checked}
				disabled={!stop.resolved}
				onCheckedChange={() => onToggle(stop.id)}
			/>
			<label
				htmlFor={rowId}
				className={`min-w-0 flex-1 ${stop.resolved ? 'cursor-pointer' : 'cursor-not-allowed opacity-60'}`}
			>
				<span className="block truncate text-sm font-medium text-text-primary">{stop.label}</span>
				{stop.sublabel && (
					<span className="block truncate text-xs text-text-muted">{stop.sublabel}</span>
				)}
				{imported && <ImportedPill />}
				{!stop.resolved && (
					<span className="mt-0.5 flex items-center gap-1 text-xs text-error-fg">
						<AlertCircle className="size-3 shrink-0" aria-hidden />
						Couldn&apos;t locate — needs coordinates
					</span>
				)}
			</label>
			{stop.resolved ? (
				<MapPin className="mt-0.5 size-4 shrink-0 text-primary" aria-hidden />
			) : (
				<AlertCircle className="mt-0.5 size-4 shrink-0 text-error-fg" aria-hidden />
			)}
		</div>
	)
}

/**
 * Compute the default selection: the next un-imported chronological leg. Stops
 * arrive date-sorted, so the first MAX_IMPORTABLE_STOPS resolved stops that
 * aren't already in `imported` are the next batch to import. Keeping this out of
 * the effect holds that callback under the complexity ceiling.
 */
function defaultSelection(stops: ImportableStop[], imported: Set<string>): Set<string> {
	return new Set(
		stops
			.filter((s) => s.resolved && !imported.has(s.id))
			.slice(0, MAX_IMPORTABLE_STOPS)
			.map((s) => s.id),
	)
}

/** Build the ordered Waypoint[] for the selected, resolved stops. */
function buildWaypoints(stops: ImportableStop[], selected: Set<string>): Waypoint[] {
	return stops
		.filter(
			(stop) => stop.resolved && stop.lat !== null && stop.lng !== null && selected.has(stop.id),
		)
		.map((stop) => ({
			name: stop.label,
			// Safe: the filter above guarantees both are non-null.
			lat: stop.lat as number,
			lng: stop.lng as number,
		}))
}

/**
 * Final step of the import wizard: choose stops and import them.
 *
 * Source-agnostic — it renders the GIVEN `stops` directly (no fetching). The
 * upstream source (file upload or pasted text) has already
 * extracted and normalized them into `ImportableStop[]`; `sourceKey` is the
 * stable identifier that scopes the imported-tracker to that source.
 *
 * Defaults the selection to the next un-imported chronological leg (so a long
 * source imported in batches picks up where the last batch left off), and lets
 * the user toggle individual stops or all at once. Already-imported stops show a
 * subtle reminder pill but stay toggleable; unresolved stops can't be selected.
 * Enforces MAX_IMPORTABLE_STOPS and a 2-stop minimum. On import it loads the
 * whole route atomically via `importStops`, kicks off a full `calculate`,
 * remembers the imported stop ids, then closes.
 */
export function StopSelectStep({ stops, sourceKey, onClose, onBack }: StopSelectStepProps) {
	const [selected, setSelected] = useState<Set<string>>(new Set())
	const [importedIds, setImportedIds] = useState<Set<string>>(new Set())

	// Synchronous double-submit guard — latches before any state flush so a
	// double-click can't fire two imports in the same tick.
	const importingRef = useRef(false)

	/**
	 * Seed the imported-tracker memory and the default selection from the given
	 * stops. Reads the remembered set BEFORE computing the default so the default
	 * selection is the next un-imported leg, not a re-run of imported stops. Runs
	 * whenever the source (`stops`/`sourceKey`) changes — e.g. the user goes Back
	 * and picks a different source.
	 */
	const seed = useCallback(() => {
		const imported = getImportedStopIds(sourceKey)
		setImportedIds(imported)
		setSelected(defaultSelection(stops, imported))
	}, [stops, sourceKey])

	useEffect(() => {
		seed()
	}, [seed])

	const resolvedStops = useMemo(() => stops.filter((s) => s.resolved), [stops])
	const selectedCount = selected.size
	const allResolvedSelected =
		resolvedStops.length > 0 && resolvedStops.every((s) => selected.has(s.id))

	const toggleStop = useCallback((id: string) => {
		setSelected((prev) => {
			const next = new Set(prev)
			if (next.has(id)) next.delete(id)
			else next.add(id)
			return next
		})
	}, [])

	const toggleAll = useCallback(() => {
		setSelected((prev) => {
			// If everything resolved is already selected, clear; otherwise select all.
			const everySelected = resolvedStops.length > 0 && resolvedStops.every((s) => prev.has(s.id))
			return everySelected ? new Set() : new Set(resolvedStops.map((s) => s.id))
		})
	}, [resolvedStops])

	const overCap = selectedCount > MAX_IMPORTABLE_STOPS
	const tooFew = selectedCount < MIN_IMPORTABLE_STOPS
	const canImport = !overCap && !tooFew

	const handleImport = useCallback(() => {
		if (importingRef.current || !canImport) return
		importingRef.current = true
		try {
			const waypoints = buildWaypoints(stops, selected)
			if (waypoints.length < MIN_IMPORTABLE_STOPS) {
				toast.error('Select at least an origin and a destination')
				return
			}
			const { importStops } = useRouteStore.getState()
			importStops(waypoints)
			// Defer the full calculation one tick so importStops' state flush
			// (origin/destination/waypoints) is committed before the pipeline reads it.
			setTimeout(() => useRouteStore.getState().calculate(), 0)
			// Remember this batch so the next open defaults to the following leg
			// and these rows show the "Imported" reminder.
			markStopsImported(sourceKey, [...selected])
			toast.success(`Imported ${waypoints.length} stops`)
			onClose()
		} finally {
			importingRef.current = false
		}
	}, [canImport, stops, selected, onClose, sourceKey])

	/**
	 * Forget every remembered import for this source, then re-seed so the
	 * selection re-defaults to the first leg — letting the user start over.
	 */
	const clearImported = useCallback(() => {
		clearImportedStops(sourceKey)
		seed()
	}, [sourceKey, seed])

	return (
		<div className="flex flex-col gap-3">
			<div className="flex items-center gap-2 border-b border-border pb-2">
				<Checkbox
					id="mt-select-all"
					checked={allResolvedSelected}
					disabled={resolvedStops.length === 0}
					onCheckedChange={toggleAll}
				/>
				<label
					htmlFor="mt-select-all"
					className="flex-1 cursor-pointer text-sm text-text-secondary"
				>
					Select all
				</label>
				<span className="text-xs text-text-muted">
					{selectedCount} of {resolvedStops.length} selected
				</span>
			</div>

			{importedIds.size > 0 && (
				<div className="flex items-center gap-2 text-xs text-text-muted">
					<span>{importedIds.size} already imported</span>
					<Button
						type="button"
						variant="link"
						size="sm"
						className="h-auto p-0 text-xs text-text-muted hover:text-text-secondary"
						onClick={clearImported}
					>
						Clear
					</Button>
				</div>
			)}

			<div className="flex max-h-72 flex-col gap-0.5 overflow-y-auto">
				{stops.map((stop) => (
					<StopRow
						key={stop.id}
						stop={stop}
						checked={selected.has(stop.id)}
						imported={importedIds.has(stop.id)}
						onToggle={toggleStop}
					/>
				))}
			</div>

			{overCap && (
				<p aria-live="polite" className="text-sm text-error-fg">
					You can import up to {MAX_IMPORTABLE_STOPS} stops. Deselect{' '}
					{selectedCount - MAX_IMPORTABLE_STOPS} to continue.
				</p>
			)}

			<div className="flex items-center gap-2">
				{onBack && (
					<Button type="button" variant="ghost" size="sm" onClick={onBack}>
						<ArrowLeft className="size-4" aria-hidden />
						Back
					</Button>
				)}
				<Button
					type="button"
					variant="default"
					className="flex-1"
					onClick={handleImport}
					disabled={!canImport}
				>
					<DownloadCloud className="size-4" aria-hidden />
					Import {selectedCount} {selectedCount === 1 ? 'stop' : 'stops'}
				</Button>
			</div>
		</div>
	)
}
